import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  getSessions,
  createSession,
  updateSession,
  deleteSession,
  analyserImportSessions,
  analyserImportSessionsSite,
  supprimerSessions,
  supprimerToutesSessions,
  texteCalendrierSite,
  enregistrerImportSessions,
} from '../services/calendrierService';
import { getFormations } from '../services/formationService';
import ImportTexteModal from '../components/ImportTexteModal';
import ConfirmationSuppression from '../components/ConfirmationSuppression';
import Agenda from './Agenda';
import SelectRecherche from '../components/SelectRecherche';
import SelectVille from '../components/SelectVille';
import '../assets/styles/Calendrier.css';
import BarreProgression, { useProgression } from '../components/BarreProgression';
import RemplirParTexte from '../components/RemplirParTexte';
import { anneeCourante, anneeCouranteDesChamps } from '../utils/anneeCourante';

const STATUTS = [
  { value: 'ouverte', label: 'Ouverte' },
  { value: 'complete', label: 'Complète' },
  { value: 'confirmee', label: 'Confirmée' },
  { value: 'annulee', label: 'Annulée' },
  { value: 'terminee', label: 'Terminée' },
];

const MODALITES = [
  { value: 'presentiel', label: 'Présentiel' },
  { value: 'inter_presentiel', label: 'Inter présentiel' },
  { value: 'distanciel', label: 'Distanciel' },
];

const PERIODES = [
  { value: 'a_venir', label: 'À venir' },
  { value: 'passees', label: 'Passées' },
  { value: '', label: 'Toutes' },
];

const SESSION_VIDE = {
  formationId: '',
  intituleCalendrier: '',
  module: '',
  publicCible: '',
  dateDebut: '',
  dateFin: '',
  nbJours: '',
  heureDebut: '09:30',
  heureFin: '17:30',
  placesMax: 12,
  placesReservees: 0,
  statut: 'ouverte',
  urlProgramme: '',
  urlReservation: '',
  commentaire: '',
  lieux: [{ ville: '', modalite: 'presentiel', adresse: '' }],
};

/** L'API renvoie des dates ISO complètes ; <input type="date"> veut AAAA-MM-JJ. */
const versDateInput = (v) => (v ? String(v).slice(0, 10) : '');
/** Et <input type="time"> veut HH:MM, pas HH:MM:SS. */
const versHeureInput = (v) => (v ? String(v).slice(0, 5) : '');

function sessionToForm(s) {
  return {
    formationId: s.formation_id ?? '',
    intituleCalendrier: s.intitule_calendrier ?? '',
    module: s.module ?? '',
    publicCible: s.public_cible ?? '',
    dateDebut: versDateInput(s.date_debut),
    dateFin: versDateInput(s.date_fin),
    nbJours: s.nb_jours ?? '',
    heureDebut: versHeureInput(s.heure_debut) || '09:30',
    heureFin: versHeureInput(s.heure_fin) || '17:30',
    placesMax: s.places_max ?? 12,
    placesReservees: s.places_reservees ?? 0,
    statut: s.statut ?? 'ouverte',
    urlProgramme: s.url_programme ?? '',
    urlReservation: s.url_reservation ?? '',
    commentaire: s.commentaire ?? '',
    lieux: (s.lieux_detail || []).length
      ? s.lieux_detail.map((l) => ({
          ville: l.ville,
          modalite: l.modalite,
          adresse: l.adresse ?? '',
        }))
      : [{ ville: '', modalite: 'presentiel', adresse: '' }],
  };
}

const libelle = (liste, valeur) => liste.find((x) => x.value === valeur)?.label || valeur;

const MOIS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

/** « septembre 2026 » — sert d'intertitre au fil du calendrier. */
function cleMois(dateIso) {
  const d = new Date(dateIso);
  return `${MOIS[d.getMonth()]} ${d.getFullYear()}`;
}

/**
 * Le bloc date de la carte. Une session sur un seul jour n'affiche qu'un
 * nombre ; sur plusieurs, l'intervalle — et le mois n'est répété que s'il
 * change en cours de route.
 */
function blocDate(s) {
  const debut = new Date(s.date_debut);
  const fin = new Date(s.date_fin);
  const memeMois = debut.getMonth() === fin.getMonth();

  if (debut.getTime() === fin.getTime()) {
    return { jours: `${debut.getDate()}`, mois: MOIS[debut.getMonth()].slice(0, 4) };
  }
  if (memeMois) {
    return {
      jours: `${debut.getDate()}–${fin.getDate()}`,
      mois: MOIS[debut.getMonth()].slice(0, 4),
    };
  }
  return {
    jours: `${debut.getDate()} ${MOIS[debut.getMonth()].slice(0, 3)}. – ${fin.getDate()}`,
    mois: MOIS[fin.getMonth()].slice(0, 4),
  };
}

function SessionsCatalogue() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [recherche, setRecherche] = useState('');
  const [statut, setStatut] = useState('');
  const [ville, setVille] = useState('');
  const [periode, setPeriode] = useState('a_venir');

  // null = modale fermée ; { id: null } = création ; { id: 12 } = édition
  const [edition, setEdition] = useState(null);
  const [form, setForm] = useState(SESSION_VIDE);
  const [saving, setSaving] = useState(false);
  const progressionEnregistrement = useProgression(saving, { estimeeMs: 3000 });
  const [formError, setFormError] = useState(null);

  const [suppression, setSuppression] = useState(null);
  // Sélection par cases : suppression groupée, ou de tout le calendrier.
  const [selection, setSelection] = useState(() => new Set());
  const [suppressionGroupee, setSuppressionGroupee] = useState(null); // 'selection' | 'tout'
  const [flash, setFlash] = useState(null);
  const [reload, setReload] = useState(0);
  // Import par copier-coller d'un catalogue de dates.
  const [importOuvert, setImportOuvert] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setRecherche(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Le catalogue alimente le sélecteur de formation du formulaire.
  useEffect(() => {
    getFormations()
      .then((res) => setFormations(res.data || []))
      .catch(() => setFormations([]));
  }, []);

  useEffect(() => {
    let annule = false;
    setLoading(true);
    setError(null);

    getSessions({ search: recherche, statut, ville, periode })
      .then((res) => {
        if (!annule) setSessions(res.data || []);
      })
      .catch(() => {
        if (!annule) setError('Impossible de charger le calendrier.');
      })
      .finally(() => {
        if (!annule) setLoading(false);
      });

    return () => {
      annule = true;
    };
  }, [recherche, statut, ville, periode, reload]);

  /** Villes réellement programmées : le filtre ne propose rien de vide. */
  const villes = useMemo(() => {
    const set = new Set();
    for (const s of sessions) for (const v of s.villes || []) set.add(v);
    return [...set].sort((a, b) => a.localeCompare(b, 'fr'));
  }, [sessions]);

  /** Les cartes sont regroupées par mois : 45 sessions à la file se lisent mal. */
  const parMois = useMemo(() => {
    const groupes = [];
    for (const s of sessions) {
      const cle = cleMois(s.date_debut);
      const dernier = groupes[groupes.length - 1];
      if (dernier && dernier.mois === cle) dernier.sessions.push(s);
      else groupes.push({ mois: cle, sessions: [s] });
    }
    return groupes;
  }, [sessions]);

  function ouvrirCreation() {
    setEdition({ id: null });
    setForm(SESSION_VIDE);
    setFormError(null);
  }

  function ouvrirEdition(s) {
    setEdition({ id: s.calendrier_id });
    setForm(sessionToForm(s));
    setFormError(null);
  }

  function fermerModale() {
    setEdition(null);
    setFormError(null);
  }

  function handleFieldChange(champ) {
    return (e) => {
      const valeur = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((prev) => ({ ...prev, [champ]: valeur }));
    };
  }

  function handleLieuChange(index, champ) {
    return (e) => {
      const valeur = e.target.value;
      setForm((prev) => ({
        ...prev,
        lieux: prev.lieux.map((l, i) => (i === index ? { ...l, [champ]: valeur } : l)),
      }));
    };
  }

  function ajouterLieu() {
    setForm((prev) => ({
      ...prev,
      lieux: [...prev.lieux, { ville: '', modalite: 'presentiel', adresse: '' }],
    }));
  }

  function retirerLieu(index) {
    setForm((prev) => ({ ...prev, lieux: prev.lieux.filter((_, i) => i !== index) }));
  }

  /**
   * Quand les deux dates sont posées, la durée se déduit — mais reste
   * modifiable : une session du vendredi au lundi ne dure pas 4 jours.
   */
  function handleDateChange(champ) {
    return (e) => {
      // À la création, une année passée (13/02/2023) devient l'année en cours.
      const valeur = edition?.id === null ? anneeCourante(e.target.value) : e.target.value;
      setForm((prev) => {
        const suivant = { ...prev, [champ]: valeur };
        if (suivant.dateDebut && suivant.dateFin) {
          const jours =
            (new Date(suivant.dateFin) - new Date(suivant.dateDebut)) / 86400000 + 1;
          if (jours > 0) suivant.nbJours = jours;
        }
        return suivant;
      });
    };
  }

  /**
   * Valeurs proposées par l'IA, fusionnées comme le font les champs : même
   * représentation que la saisie (chaînes pour les nombres et la liste de
   * formations), heures remises en « hh:mm » pour <input type="time">, et durée
   * déduite des dates quand le texte n'en donne pas.
   */
  function appliquerValeursIA(v) {
    const valeurs = edition?.id === null ? anneeCouranteDesChamps(v, ['dateDebut', 'dateFin']) : { ...v };
    for (const nom of ['heureDebut', 'heureFin']) {
      if (!(nom in valeurs)) continue;
      const m = /^(\d{1,2}):(\d{2})/.exec(String(valeurs[nom]).trim());
      if (m && Number(m[1]) < 24 && Number(m[2]) < 60) valeurs[nom] = `${m[1].padStart(2, '0')}:${m[2]}`;
      else delete valeurs[nom];
    }
    for (const nom of ['formationId', 'nbJours', 'placesMax', 'placesReservees']) {
      if (nom in valeurs) valeurs[nom] = String(valeurs[nom]);
    }
    setForm((prev) => {
      const suivant = { ...prev, ...valeurs };
      if (!('nbJours' in valeurs) && suivant.dateDebut && suivant.dateFin) {
        const jours = (new Date(suivant.dateFin) - new Date(suivant.dateDebut)) / 86400000 + 1;
        if (jours > 0) suivant.nbJours = jours;
      }
      return suivant;
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);

    const payload = {
      ...(edition.id === null ? anneeCouranteDesChamps(form, ['dateDebut', 'dateFin']) : form),
      formationId: Number(form.formationId),
      nbJours: Number(form.nbJours),
      placesMax: Number(form.placesMax),
      placesReservees: Number(form.placesReservees),
      module: form.module || null,
      publicCible: form.publicCible || null,
      // Un lieu laissé vide est une ligne que l'utilisateur n'a pas remplie,
      // pas une saisie à valider.
      lieux: form.lieux.filter((l) => l.ville.trim()),
    };

    try {
      if (edition.id === null) {
        await createSession(payload);
        setFlash('Session programmée.');
      } else {
        await updateSession(edition.id, payload);
        setFlash('Session mise à jour.');
      }
      setReload((n) => n + 1);
      setEdition(null);
    } catch (err) {
      setFormError(err.message || 'Une erreur est survenue.');
    } finally {
      setSaving(false);
    }
  }

  function basculer(id) {
    setSelection((prev) => {
      const suite = new Set(prev);
      if (suite.has(id)) suite.delete(id);
      else suite.add(id);
      return suite;
    });
  }

  async function handleSupprimerGroupe() {
    setSaving(true);
    try {
      const res =
        suppressionGroupee === 'tout'
          ? await supprimerToutesSessions()
          : await supprimerSessions([...selection]);
      const b = res.data || {};
      setFlash(
        `${b.supprimees ?? 0} session${(b.supprimees ?? 0) > 1 ? 's' : ''} supprimée${(b.supprimees ?? 0) > 1 ? 's' : ''}` +
          (b.conservees ? `, ${b.conservees} conservée${b.conservees > 1 ? 's' : ''} (places réservées)` : '') +
          '.'
      );
      setSelection(new Set());
      setSuppressionGroupee(null);
      setReload((n) => n + 1);
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
      setSuppressionGroupee(null);
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    try {
      await deleteSession(suppression.calendrier_id);
      setReload((n) => n + 1);
      setFlash('Session supprimée.');
      setSuppression(null);
    } catch (err) {
      setError(err.message || 'Suppression impossible.');
      setSuppression(null);
    } finally {
      setSaving(false);
    }
  }

  const filtreActif = Boolean(recherche.trim() || statut || ville || periode !== 'a_venir');

  return (
    <div className="cal-page cal-page-onglet">
      <div className="cal-header cal-header-onglet">
        <p className="cal-subtitle">
          Sessions INTER à date ferme (les INTRA n’y figurent pas).
        </p>
        <div className="cal-header-actions">
          <button
            type="button"
            className="cal-btn"
            onClick={() => setImportOuvert('site')}
            title="Lit le calendrier sur echo-cse.fr et propose les sessions à relire"
          >
            Importer depuis le site
          </button>
          <button
            type="button"
            className="cal-btn"
            onClick={() => setImportOuvert('texte')}
            title="Collez un texte listant des dates : l’IA propose les sessions à relire"
          >
            Importer depuis un texte
          </button>
          <button type="button" className="cal-btn cal-btn-primary" onClick={ouvrirCreation}>
            + Nouvelle session
          </button>
        </div>
      </div>

      <div className="cal-toolbar">
        <input
          type="text"
          className="cal-search"
          placeholder="Rechercher une session, un lieu..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="cal-filtres">
          <label className="cal-filtre">
            <span>Période</span>
            <SelectRecherche value={periode} onChange={(e) => setPeriode(e.target.value)}>
              {PERIODES.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </SelectRecherche>
          </label>

          <label className="cal-filtre">
            <span>État</span>
            <SelectRecherche value={statut} onChange={(e) => setStatut(e.target.value)}>
              <option value="">Tous</option>
              {STATUTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </SelectRecherche>
          </label>

          <label className="cal-filtre">
            <span>Lieu</span>
            <SelectRecherche value={ville} onChange={(e) => setVille(e.target.value)}>
              <option value="">Tous</option>
              {villes.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </SelectRecherche>
          </label>

          {filtreActif && (
            <button
              type="button"
              className="cal-filtre-clear"
              onClick={() => {
                setSearch('');
                setStatut('');
                setVille('');
                setPeriode('a_venir');
              }}
            >
              Réinitialiser
            </button>
          )}
        </div>
      </div>

      <div className="cal-recap">
        <span>
          {sessions.length} session{sessions.length > 1 ? 's' : ''}
          {periode === 'a_venir' ? ' à venir' : periode === 'passees' ? ' passée(s)' : ''}
        </span>
        {flash && <span className="cal-flash">{flash}</span>}
      </div>

      <div className="cal-scroll">
        {loading && <div className="cal-state">Chargement du calendrier...</div>}
        {!loading && error && <div className="cal-state cal-state-error">{error}</div>}

        {!loading && !error && sessions.length === 0 && (
          <div className="cal-empty">
            <div className="cal-empty-title">Aucune session ne correspond à cette vue.</div>
            <div className="cal-empty-subtitle">
              Programmez une session ou élargissez la période.
            </div>
          </div>
        )}

        {!loading && !error && sessions.length > 0 && (
          <div className="cal-selection">
            <button
              type="button"
              className="cal-btn cal-btn-sm"
              onClick={() =>
                setSelection(
                  selection.size === sessions.length
                    ? new Set()
                    : new Set(sessions.map((x) => x.calendrier_id))
                )
              }
            >
              {selection.size === sessions.length ? 'Tout désélectionner' : 'Tout sélectionner'}
            </button>
            <button
              type="button"
              className="cal-btn cal-btn-sm cal-btn-danger"
              onClick={() => setSuppressionGroupee('selection')}
              disabled={selection.size === 0}
            >
              Supprimer la sélection{selection.size ? ` (${selection.size})` : ''}
            </button>
            <button
              type="button"
              className="cal-btn cal-btn-sm cal-btn-danger"
              onClick={() => setSuppressionGroupee('tout')}
              title="Vider tout le calendrier ; une session avec des places réservées est conservée"
            >
              Supprimer toutes les sessions
            </button>
          </div>
        )}

        {!loading &&
          !error &&
          parMois.map((groupe) => (
            <section className="cal-mois" key={groupe.mois}>
              <h2 className="cal-mois-titre">
                {groupe.mois}
                <span>{groupe.sessions.length}</span>
              </h2>

              <div className="cal-grille">
                {groupe.sessions.map((s) => {
                  const date = blocDate(s);
                  const complet = s.places_restantes <= 0;

                  return (
                    <article
                      className={`cal-carte${selection.has(s.calendrier_id) ? ' cal-carte-selectionnee' : ''}`}
                      key={s.calendrier_id}
                    >
                      <div className="cal-carte-haut">
                        <input
                          type="checkbox"
                          className="cal-carte-case"
                          checked={selection.has(s.calendrier_id)}
                          onChange={() => basculer(s.calendrier_id)}
                          aria-label={`Sélectionner ${s.intitule_calendrier}`}
                        />
                        <div className="cal-date" aria-hidden="true">
                          <span className="cal-date-jours">{date.jours}</span>
                          <span className="cal-date-mois">{date.mois}</span>
                        </div>

                        <div className="cal-carte-titre-bloc">
                          <div className="cal-carte-meta">
                            <span className="cal-code">{s.formation_code}</span>
                            {s.module && <span className="cal-module">{s.module}</span>}
                          </div>
                          <h3 className="cal-carte-titre">{s.intitule_calendrier}</h3>
                          {s.public_cible && (
                            <div className="cal-public">{s.public_cible}</div>
                          )}
                        </div>

                        <span className={`cal-pill cal-pill-${s.statut}`}>
                          {libelle(STATUTS, s.statut)}
                        </span>
                      </div>

                      <dl className="cal-infos">
                        <div>
                          <dt>Durée</dt>
                          <dd>
                            {Number(s.nb_jours)} j · {versHeureInput(s.heure_debut)}–
                            {versHeureInput(s.heure_fin)}
                          </dd>
                        </div>
                        <div>
                          <dt>Lieu</dt>
                          <dd>{s.lieux || 'À définir'}</dd>
                        </div>
                        <div>
                          <dt>Places</dt>
                          <dd className={complet ? 'cal-places-complet' : undefined}>
                            {complet
                              ? 'Complet'
                              : `${s.places_restantes} sur ${s.places_max} disponibles`}
                          </dd>
                        </div>
                      </dl>

                      {/* Jauge : le remplissage se lit plus vite qu'un ratio. */}
                      <div
                        className="cal-jauge"
                        title={`${s.places_reservees} place(s) réservée(s) sur ${s.places_max}`}
                      >
                        <span
                          style={{
                            width: `${Math.min(
                              100,
                              (s.places_reservees / s.places_max) * 100
                            )}%`,
                          }}
                        />
                      </div>

                      {s.commentaire && <p className="cal-commentaire">{s.commentaire}</p>}

                      <div className="cal-carte-actions">
                        <button
                          type="button"
                          className="cal-btn cal-btn-sm"
                          onClick={() => navigate(`/calendrier/session/catalogue/${s.calendrier_id}`)}
                          title="Feuilles de présence, participants et formateurs de cette session"
                        >
                          Détails
                        </button>
                        <button
                          type="button"
                          className="cal-btn cal-btn-sm"
                          onClick={() => ouvrirEdition(s)}
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          className="cal-btn cal-btn-sm cal-btn-danger"
                          onClick={() => setSuppression(s)}
                        >
                          Supprimer
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          ))}
      </div>

      {edition && (
        <div className="cal-modal-overlay" onClick={fermerModale}>
          <div className="cal-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cal-modal-header">
              <h2 className="cal-modal-title">
                {edition.id === null ? 'Nouvelle session' : 'Modifier la session'}
              </h2>
              <button type="button" className="cal-modal-close" onClick={fermerModale}>
                ×
              </button>
            </div>

            <form className="cal-form cal-modal-body" onSubmit={handleSubmit}>
              {/* En tête de fiche, et collée là pendant le défilement : sous le dernier champ, on ne la voyait pas. */}
              {formError && <div className="cal-form-message">{formError}</div>}

              {/* À la création comme à la modification : le mail ou le message collé suffit à remplir la session. */}
              <RemplirParTexte
                contexte="session de formation inter du calendrier"
                champs={[
                  {
                    cle: 'formationId',
                    libelle: 'Formation au catalogue',
                    type: 'choix',
                    options: formations.map((f) => ({ valeur: String(f.id), libelle: `${f.code} — ${f.intitule}` })),
                  },
                  { cle: 'module', libelle: 'Module' },
                  { cle: 'intituleCalendrier', libelle: 'Libellé affiché au calendrier' },
                  { cle: 'publicCible', libelle: 'Public visé' },
                  { cle: 'dateDebut', libelle: 'Du', type: 'date' },
                  { cle: 'dateFin', libelle: 'Au', type: 'date' },
                  { cle: 'nbJours', libelle: 'Durée (jours)', type: 'nombre' },
                  { cle: 'heureDebut', libelle: 'Heure de début (hh:mm, 24 h)' },
                  { cle: 'heureFin', libelle: 'Heure de fin (hh:mm, 24 h)' },
                  { cle: 'placesMax', libelle: 'Places au total', type: 'nombre' },
                  { cle: 'placesReservees', libelle: 'Places réservées', type: 'nombre' },
                  {
                    cle: 'statut',
                    libelle: 'État',
                    type: 'choix',
                    options: STATUTS.map((s) => ({ valeur: s.value, libelle: s.label })),
                  },
                  { cle: 'urlProgramme', libelle: 'Lien programme' },
                  { cle: 'urlReservation', libelle: 'Lien réservation' },
                  { cle: 'commentaire', libelle: 'Commentaire' },
                ]}
                onValeurs={appliquerValeursIA}
              />

              <div className="cal-form-section">Formation</div>
              <div className="cal-form-row">
                <label className="cal-field cal-field-grow">
                  <span className="cal-label">Formation au catalogue</span>
                  <SelectRecherche
                    className="cal-select"
                    value={form.formationId}
                    onChange={handleFieldChange('formationId')}
                    required
                  >
                    <option value="" disabled>
                      Sélectionner…
                    </option>
                    {formations.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.code} — {f.intitule}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>

                <label className="cal-field">
                  <span className="cal-label">Module</span>
                  <input
                    type="text"
                    className="cal-input"
                    placeholder="Module 1"
                    value={form.module}
                    onChange={handleFieldChange('module')}
                  />
                </label>
              </div>

              <label className="cal-field">
                <span className="cal-label">Libellé affiché au calendrier</span>
                <input
                  type="text"
                  className="cal-input"
                  placeholder="SSCT -50 salariés (Module 1)"
                  value={form.intituleCalendrier}
                  onChange={handleFieldChange('intituleCalendrier')}
                  required
                />
              </label>
              <p className="cal-hint">
                Libellé du site, conservé tel quel pour vérifier le rattachement.
              </p>

              <label className="cal-field">
                <span className="cal-label">Public visé</span>
                <input
                  type="text"
                  className="cal-input"
                  placeholder="Plus de 50 salariés"
                  value={form.publicCible}
                  onChange={handleFieldChange('publicCible')}
                />
              </label>

              <div className="cal-form-section">Dates et horaires</div>
              <div className="cal-form-row">
                <label className="cal-field">
                  <span className="cal-label">Du</span>
                  <input
                    type="date"
                    className="cal-input"
                    value={form.dateDebut}
                    onChange={handleDateChange('dateDebut')}
                    required
                  />
                </label>
                <label className="cal-field">
                  <span className="cal-label">Au</span>
                  <input
                    type="date"
                    className="cal-input"
                    value={form.dateFin}
                    min={form.dateDebut || undefined}
                    onChange={handleDateChange('dateFin')}
                    required
                  />
                </label>
                <label className="cal-field">
                  <span className="cal-label">Durée (jours)</span>
                  <input
                    type="number"
                    step="0.5"
                    min="0.5"
                    className="cal-input"
                    value={form.nbJours}
                    onChange={handleFieldChange('nbJours')}
                    required
                  />
                </label>
                <label className="cal-field">
                  <span className="cal-label">Début</span>
                  <input
                    type="time"
                    className="cal-input"
                    value={form.heureDebut}
                    onChange={handleFieldChange('heureDebut')}
                  />
                </label>
                <label className="cal-field">
                  <span className="cal-label">Fin</span>
                  <input
                    type="time"
                    className="cal-input"
                    value={form.heureFin}
                    onChange={handleFieldChange('heureFin')}
                  />
                </label>
              </div>
              <p className="cal-hint">
                Durée déduite des dates, modifiable.
              </p>

              <div className="cal-form-section">Lieux</div>
              {form.lieux.map((lieu, index) => (
                <div className="cal-lieu" key={index}>
                  <div className="cal-form-row">
                    <label className="cal-field">
                      <span className="cal-label">Ville</span>
                      <SelectVille
                        className="cal-input"
                        placeholder="Paris"
                        value={lieu.ville}
                        onChange={handleLieuChange(index, 'ville')}
                      />
                    </label>
                    <label className="cal-field">
                      <span className="cal-label">Modalité</span>
                      <SelectRecherche
                        className="cal-select"
                        value={lieu.modalite}
                        onChange={handleLieuChange(index, 'modalite')}
                      >
                        {MODALITES.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.label}
                          </option>
                        ))}
                      </SelectRecherche>
                    </label>
                    <label className="cal-field cal-field-grow">
                      <span className="cal-label">Adresse</span>
                      <input
                        type="text"
                        className="cal-input"
                        value={lieu.adresse}
                        onChange={handleLieuChange(index, 'adresse')}
                      />
                    </label>
                    {form.lieux.length > 1 && (
                      <button
                        type="button"
                        className="cal-btn cal-btn-sm cal-btn-danger cal-lieu-retirer"
                        onClick={() => retirerLieu(index)}
                      >
                        Retirer
                      </button>
                    )}
                  </div>
                </div>
              ))}
              <button
                type="button"
                className="cal-btn cal-btn-sm cal-ajouter-lieu"
                onClick={ajouterLieu}
              >
                + Ajouter un lieu
              </button>

              <div className="cal-form-section">Places et état</div>
              <div className="cal-form-row">
                <label className="cal-field">
                  <span className="cal-label">Places au total</span>
                  <input
                    type="number"
                    min="1"
                    className="cal-input"
                    value={form.placesMax}
                    onChange={handleFieldChange('placesMax')}
                    required
                  />
                </label>
                <label className="cal-field">
                  <span className="cal-label">Places réservées</span>
                  <input
                    type="number"
                    min="0"
                    max={form.placesMax}
                    className="cal-input"
                    value={form.placesReservees}
                    onChange={handleFieldChange('placesReservees')}
                  />
                </label>
                <label className="cal-field">
                  <span className="cal-label">État</span>
                  <SelectRecherche
                    className="cal-select"
                    value={form.statut}
                    onChange={handleFieldChange('statut')}
                  >
                    {STATUTS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </SelectRecherche>
                </label>
              </div>

              <div className="cal-form-section">Liens et notes</div>
              <div className="cal-form-row">
                <label className="cal-field cal-field-grow">
                  <span className="cal-label">Lien programme</span>
                  <input
                    type="url"
                    className="cal-input"
                    value={form.urlProgramme}
                    onChange={handleFieldChange('urlProgramme')}
                  />
                </label>
                <label className="cal-field cal-field-grow">
                  <span className="cal-label">Lien réservation</span>
                  <input
                    type="url"
                    className="cal-input"
                    value={form.urlReservation}
                    onChange={handleFieldChange('urlReservation')}
                  />
                </label>
              </div>

              <label className="cal-field">
                <span className="cal-label">Commentaire</span>
                <textarea
                  className="cal-textarea"
                  rows={2}
                  value={form.commentaire}
                  onChange={handleFieldChange('commentaire')}
                />
              </label>

              <BarreProgression valeur={progressionEnregistrement} libelle="Enregistrement de la session" estimation />
              <div className="cal-form-actions">
                <button type="button" className="cal-btn" onClick={fermerModale} disabled={saving}>
                  Annuler
                </button>
                <button type="submit" className="cal-btn cal-btn-primary" disabled={saving}>
                  {saving ? 'Enregistrement' : edition.id === null ? 'Programmer' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {suppression && (
        <div className="cal-modal-overlay" onClick={() => setSuppression(null)}>
          <div className="cal-modal cal-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="cal-modal-header">
              <h2 className="cal-modal-title">Supprimer la session</h2>
            </div>
            <div className="cal-modal-body">
              <p className="cal-confirm-text">
                Voulez-vous vraiment supprimer <strong>{suppression.intitule_calendrier}</strong>{' '}
                du {versDateInput(suppression.date_debut).split('-').reverse().join('/')} ? Elle
                est mise à la corbeille, restaurable pendant 30 jours.
              </p>
              <div className="cal-form-actions">
                <button
                  type="button"
                  className="cal-btn"
                  onClick={() => setSuppression(null)}
                  disabled={saving}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="cal-btn cal-btn-danger-solid"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  {saving ? 'Suppression' : 'Supprimer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {suppressionGroupee && (
        <ConfirmationSuppression
          titre={
            suppressionGroupee === 'tout'
              ? 'Supprimer toutes les sessions du calendrier ?'
              : `Supprimer ${selection.size} session${selection.size > 1 ? 's' : ''} ?`
          }
          sousTitre={suppressionGroupee === 'tout' ? `${sessions.length} session${sessions.length > 1 ? 's' : ''} affichée${sessions.length > 1 ? 's' : ''}` : undefined}
          details={['Les sessions, leurs lieux et leurs places.']}
          note="Une session avec des places réservées est conservée : annulez-la plutôt."
          enCours={saving}
          onAnnuler={() => setSuppressionGroupee(null)}
          onConfirmer={handleSupprimerGroupe}
        />
      )}

      {importOuvert && (
        <ImportTexteModal
          titre="Importer des sessions depuis un texte"
          aide="Collez un texte listant des dates : l’IA propose les sessions, à relire avant d’enregistrer."
          exemple={
            'Exemple :\nSSCT 50 à 299 salariés (Module 1) — 17 et 18 mars 2027 — Paris\nTrésorerie du CSE — 3 avril 2027 — Paris ou en ligne\n…'
          }
          motPluriel="sessions"
          cleObjet="session"
          colonnes={[
            {
              cle: 'formationId',
              zone: 'titre',
              resume: true,
              label: 'Formation',
              type: 'select',
              numerique: true,
              obligatoire: true,
              vide: '— Choisir —',
              largeur: 220,
              options: formations.map((f) => ({ value: f.id, label: `${f.code} — ${f.intitule}` })),
            },
            { cle: 'intituleCalendrier', resume: true, label: 'Libellé', obligatoire: true, largeur: 200, zone: 'titre', grand: true },
            { cle: 'module', resume: true, label: 'Module', largeur: 90 },
            { cle: 'dateDebut', resume: true, label: 'Début', type: 'date', obligatoire: true, largeur: 140 },
            { cle: 'dateFin', resume: true, label: 'Fin', type: 'date', obligatoire: true, largeur: 140 },
            { cle: 'nbJours', resume: true, label: 'Jours', type: 'number', pas: '0.5', min: 0.5, obligatoire: true, largeur: 70 },
            { cle: 'placesMax', label: 'Places', type: 'number', pas: '1', min: 1, largeur: 70 },
            { cle: 'lieux', resume: true, label: 'Villes', type: 'lieux', largeur: 160 },
            { cle: 'publicCible', label: 'Public', largeur: 130 },
          ]}
          chargerTexte={{ libelle: 'Récupérer le calendrier sur echo-cse.fr', charger: texteCalendrierSite }}
          analyserSource={() => analyserImportSessionsSite().then((res) => res.data)}
          demarrerDepuisSource={importOuvert === 'site'}
          remplacement={{
            libelle: 'Supprimer d’abord toutes les sessions du calendrier',
            note: 'Une session avec des places réservées est conservée.',
            supprimerTout: () => supprimerToutesSessions().then((res) => res.data),
            libelleBilan: (b) => `${b.supprimees} supprimée${b.supprimees > 1 ? 's' : ''}${b.conservees ? `, ${b.conservees} conservée${b.conservees > 1 ? 's' : ''} (réservées)` : ''}`,
          }}
          analyser={(texte) => analyserImportSessions(texte).then((res) => res.data)}
          enregistrer={(retenues) =>
            enregistrerImportSessions(retenues.map((p) => p.session)).then((res) => res.data)
          }
          onFermer={() => setImportOuvert(false)}
          onEnregistre={(bilan) => {
            if (bilan.creees > 0) {
              setFlash(`${bilan.creees} session${bilan.creees > 1 ? 's' : ''} programmée${bilan.creees > 1 ? 's' : ''} depuis le texte.`);
              setReload((n) => n + 1);
            }
          }}
        />
      )}
    </div>
  );
}

const ONGLETS = [
  { cle: 'agenda', label: 'Agenda' },
  { cle: 'catalogue', label: 'Sessions en inter' },
];

/**
 * Deux vues du même temps : l'agenda, qui montre ce qui se passe jour par
 * jour toutes sources confondues, et le calendrier des sessions INTER, qui
 * gère l'offre publiée. L'agenda ouvre en premier — c'est celui qu'on
 * consulte tous les jours ; l'autre ne se touche qu'à la programmation.
 */
function Calendrier() {
  // ?onglet=catalogue : arrivée depuis la recherche globale sur une session.
  const [params] = useSearchParams();
  const [onglet, setOnglet] = useState(() =>
    params.get('onglet') === 'catalogue' ? 'catalogue' : 'agenda'
  );
  useEffect(() => {
    if (params.get('onglet') === 'catalogue') setOnglet('catalogue');
  }, [params]);

  return (
    <div className="cal-page">
      <div className="cal-header">
        <div>
          <h1 className="cal-title">Calendrier</h1>
        </div>
      </div>

      <nav className="cal-onglets">
        {ONGLETS.map((o) => (
          <button
            key={o.cle}
            type="button"
            className={'cal-onglet' + (onglet === o.cle ? ' cal-onglet-actif' : '')}
            onClick={() => setOnglet(o.cle)}
          >
            {o.label}
          </button>
        ))}
      </nav>

      {onglet === 'agenda' ? <Agenda /> : <SessionsCatalogue />}
    </div>
  );
}

export default Calendrier;
